import { normalizeTargets } from '@ts/utils';

export class ComponentRegistry<T> {
  private registry = new Map<string, T>();
  private elementToIdMap = new Map<HTMLElement, string>();
  private instanceCounter = 0;
  private readonly prefix: string;

  constructor(prefix: string) {
    this.prefix = prefix;
  }

  public register(element: HTMLElement, instance: T): string {
    if (!element.id) {
      element.id = `${this.prefix}-${this.instanceCounter++}`;
    }
    this.registry.set(element.id, instance);
    this.elementToIdMap.set(element, element.id);
    return element.id;
  }

  public get(id: string): T | null {
    return this.registry.get(id) ?? null;
  }

  public unregister(id: string): boolean {
    const element = Array.from(this.elementToIdMap.entries())
      .find(([, elId]) => elId === id)?.[0];
    
    if (element) {
      this.elementToIdMap.delete(element);
    }
    
    return this.registry.delete(id);
  }

  public clear(): void {
    this.registry.clear();
    this.elementToIdMap.clear();
  }

  public getIds(): string[] {
    return Array.from(this.registry.keys());
  }

  public static processTargets<T, E extends HTMLElement>(
    registry: ComponentRegistry<T>,
    target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
    filterTypes?: Array<new (...args: any[]) => HTMLElement>
  ): E[] | null {
    const elements = normalizeTargets(target, filterTypes) as E[];
    const unregisteredItems = registry.filterUnregistered(elements);
    return unregisteredItems;
  }

  public has(element: HTMLElement): boolean {
    return this.elementToIdMap.has(element);
  }

  public filterUnregistered<E extends HTMLElement>(elements: E[]): E[] {
    return elements.filter(element => !this.has(element));
  }
}